import { DOCUMENT } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { ENVIRONMENT_CONFIG } from '@core/config-angular';
import { getQueryParameterHistory } from '@core/jss-routing';
import type { GenericFormToGenericFormSubmitSettings } from '@innogy/sitecore-forms/models';
import { LocationService } from '@core/jss-seo';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import capitalize from 'lodash/capitalize';
import { MarkAsSubmittedAction, ResetAction } from 'ngrx-forms';
import { delay, filter, map, mergeMap, tap } from 'rxjs/operators';
import { FehApiActions } from '@feh/shared-api';

import { genericFormToGenericFormSubmit } from './generic-form-to-generic-form-submit';
import {
  markGenericFormAsPostedAction,
  resetGenericFormAction,
  submitGenericFormAction,
} from './generic-form.actions';
import { selectFormStateForForm } from './helpers/generic-form-utils';
import { GenericFormSubmitFlowService } from './api/generic-form-submit-flow.service';
import { GenericApiActions } from './api/generic.ngrx-wrapper';

@Injectable()
export class GenericFormEffects {
  private readonly actions$ = inject(Actions);
  private readonly document = inject(DOCUMENT);
  private readonly config = inject(ENVIRONMENT_CONFIG);
  private readonly location = inject(LocationService);
  private readonly store$ = inject(Store);
  private readonly genericFormSubmitFlow = inject(GenericFormSubmitFlowService);

  queryParamHistory$ = this.store$.select(getQueryParameterHistory);

  public readonly markGenericFormAsSubmitted$ = createEffect(() =>
    this.actions$.pipe(
      ofType(submitGenericFormAction),
      map(({ formGroupState }) => new MarkAsSubmittedAction(formGroupState.id))
    )
  );

  public readonly callGenericFormEndpoint$ = createEffect(() => {
    const flow = this.genericFormSubmitFlow;
    return this.actions$.pipe(
      ofType(submitGenericFormAction),
      filter(({ formGroupState }) => formGroupState.isValid),
      concatLatestFrom(() => [this.queryParamHistory$]),
      mergeMap(
        ([
          { integrationSettingsPath, formGroupState, formId, e2eTrackingId },
          queryParamHistory,
        ]) => {
          const settings: GenericFormToGenericFormSubmitSettings = {
            brand: this.config.brand,
            environment: capitalize(this.config.segment),
            referrer: this.document.referrer,
            formSettingsPath: integrationSettingsPath,
            e2eTrackingId,
            gclid: queryParamHistory['gclid'] ?? '',
          };
          const payload = genericFormToGenericFormSubmit(
            settings,
            formGroupState.controls
          );

          return flow.getFormIntegration(integrationSettingsPath).pipe(
            map((formIntegration) => {
              if (
                formIntegration.useV4Lambda === false ||
                flow.isSuiteCrmForm(formIntegration)
              ) {
                return FehApiActions.postGenericFormSubmit.requestAction({
                  actionId: formId,
                  noCache: true,
                  payload: { requestBody: payload },
                });
              } else {
                return GenericApiActions.postFormsubmitV4.requestAction({
                  actionId: formId,
                  noCache: true,
                  payload: {
                    requestBody: payload,
                  },
                });
              }
            })
          );
        }
      )
    );
  });

  public readonly markFormAsPosted$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        FehApiActions.postGenericFormSubmit.successAction,
        GenericApiActions.postFormsubmitV4.successAction
      ),
      filter((action) => !!action.actionId),
      concatLatestFrom(
        ({ actionId: formId }) =>
          selectFormStateForForm(formId as string, this.store$).asObservable
      ),
      mergeMap(([, { form }]) => [
        markGenericFormAsPostedAction({ formId: form.formState.id }),
      ])
    );
  });

  public readonly handleFormSubmissionSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        FehApiActions.postGenericFormSubmit.successAction,
        GenericApiActions.postFormsubmitV4.successAction
      ),
      filter((action) => !!action.actionId),
      concatLatestFrom(
        ({ actionId: formId }) =>
          selectFormStateForForm(formId as string, this.store$).asObservable
      ),
      // The side-effect of navigating is moved into a tap because the rest of the flow
      // is equal for both routed and non-routed forms.
      tap(([, formState]) => {
        if (formState.feedback?.redirectOnSuccessPage?.href) {
          this.location.navigateScLink(
            formState.feedback?.redirectOnSuccessPage
          );
        }
      }),
      // This delay is introduced to prevent flickering.
      // Since routed forms should not show the reset state before routing
      // and forms with a 'layover' on submission don't show it either,
      // this workaround achieves a stable UX pattern.
      delay(2000),
      mergeMap(([_, { form }]) => [
        resetGenericFormAction({ formId: form.formState.id }),
      ])
    );
  });

  public readonly resetGenericForm$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(resetGenericFormAction),
      mergeMap(({ formId }) => [new ResetAction(formId)])
    );
  });
}
